import React, { useEffect, useMemo, useState } from 'react';
import { CollectionGroup, NamedCollectionGroup, GenderCollectionGroup, NamedGroupPlaceholder, NoGroupItem, CollectionGroupPlaceholder, GenderCollectionGroupPlaceholder, CollectionReptileTable } from '../utilities/CollectionGroup.tsx';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { ModuleTitle } from '../utilities/ModuleTitle.tsx';
import { GroupModal } from './modals/GroupModal.tsx';
import { AddPickModal } from './modals/AddPickModal.tsx';
import { CollectionReptileModal } from './modals/CollectionReptileModal.tsx';
import { ResultsModal } from '../utilities/ResultsModal.tsx';
import { Offcanvas, Button, FormControl, InputGroup } from 'react-bootstrap';
import { RepToolDropdown } from '../utilities/shared_components/RepToolDropdown.tsx';
import { Carousel } from 'react-responsive-carousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faQuestion, faTimes, faBars } from '@fortawesome/free-solid-svg-icons'
import { SoldModal } from './modals/SoldModal.tsx';
import { ConfirmDeleteModal } from './modals/ConfirmDeleteModal.tsx';
import { CollectionReptile, CollectionReptileGroup } from './types/CollectionTypes.ts';
import { Morph } from '../../types/Morph.ts';
import { Gene } from '../../types/Gene.ts';
import { Reptile } from '../../types/Reptile.ts';
import { driver } from 'driver.js';
import { LabelModal } from './modals/LabelModal.tsx';
import { ScanModal } from './modals/ScanModal.tsx';
import { ShareAvailableReptilesModal } from './modals/ShareAvailableReptilesModal.tsx';
import { AllPairingsModal } from '../family_tree/Pairings.tsx';
import { CollectionStatsModal } from './modals/CollectionStatsModal.tsx';
import { toTitleCase } from '../utilities/TextFormatters.ts';
import { UserInfo } from '../../types/UserInfo.ts';
import { SubscriptionModal } from '../stripe/SubscriptionModal.tsx';
import { useRepToolContext } from '../../RepToolContext.tsx';

interface SortedGroups {
  available: (CollectionReptileGroup | CollectionReptile)[],
  sold: (CollectionReptileGroup | CollectionReptile)[],
  nfs: (CollectionReptileGroup | CollectionReptile)[],
}
type SelectedGrouping = "Morph" | "Name" | "Sex"


const applySearchFilter = (reptiles: CollectionReptile[], searchQuery: string, sexFilter: "male" | "female" | "unsexed" | null, selectedGrouping: SelectedGrouping) => {
  if (searchQuery === "" && !sexFilter) {
    return reptiles;
  }
  const filteredReptiles = reptiles.filter((reptile) => {
    const searchKeys = ["name", "morph_name"]
    if (selectedGrouping.includes("Name")) {
      if (sexFilter) {
        if (sexFilter === "male" || sexFilter === "female") {
          return sexFilter === reptile.gender
        } else {
          // it has to be done this way because unsexed may be called none or other things
          // so we just check like this until we can get the backend to return unsexed
          return reptile.gender !== "male" && reptile.gender !== "female";
        }
      }

    }
    for (const label of reptile.labels) {
      if (label.text.toLowerCase().includes(searchQuery.toLowerCase())) {
        return true;
      }
    }
    if (searchQuery !== "") {
      for (const key of searchKeys) {
        if (reptile[key].toLowerCase().includes(searchQuery.toLowerCase())) {
          return true;
        }
      }
      // check the genes for the search query
      for (const gene of reptile.genes) {
        if (gene.gene_name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return true;
        }
      }

      return false;
    }
    return true;
  });
  return filteredReptiles;
}


const groupings = [
  { label: "Morph", grouping: "Morph" },
  { label: "Name", grouping: "Name" },
  { label: "Sex", grouping: "Sex" },
  { label: "Name Table", grouping: "Name" },
]
interface TabType {
  label: string,
  status: string,
}

const CollectionGroupFilter = ({
  changeGrouping,
  grouping,
  isSpecies
}: {
  changeGrouping: (grouping: string) => void,
  grouping: string,
  isSpecies: boolean
}) => {
  return (
    <div>
      <select
        id="organizeStyle"
        value={grouping}
        onChange={(event) => { localStorage.setItem("groupMethod", event.target.value); changeGrouping(event.target.value) }}
        className=""
      >
        {groupings.map((grouping) => (
          <option key={grouping.label} value={grouping.label}>
            {isSpecies && grouping.label === 'Morph' ? 'Species' : grouping.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export const ToolMenuButton = ({
  clickFunction,
  subscriberOnly = false,
  children,
  userInfo
}: {
  clickFunction: () => void,
  subscriberOnly?: boolean,
  children?: React.ReactNode,
  userInfo?: UserInfo
}) => {
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);

  return <>
    {openSubscriptionModal && userInfo && <SubscriptionModal displayFeatureRequiresMessage={true} userInfo={userInfo} closeModal={() => setOpenSubscriptionModal(false)} />}
    <Button
      variant="outline-light"
      onClick={() => {
        if (subscriberOnly) {
          setOpenSubscriptionModal(true);
        } else {
          clickFunction();
        }
      }}
      className="w-100 mb-3"
    >
      {children} {subscriberOnly && <small>Upgrade to Access</small>}
    </Button>
  </>
}



const CollectionFloatButtons = ({
  toggleTutorial,
  setShowAddPickModal,
  setShowMenu,
}: {
  toggleTutorial: () => void,
  setShowAddPickModal: () => void,
  setShowMenu: () => void,
}) => (
  <div className="d-flex flex-row">
    <div
      className="mr-4 my-auto"
      onClick={() => toggleTutorial()}
    >
      <FontAwesomeIcon size='xl' icon={faQuestion} />
    </div>
    <div
      id='reptile-pick'
      className="mr-4 my-auto"
      onClick={() => setShowMenu()}
    >
      <FontAwesomeIcon size='xl' icon={faBars} />
    </div>
    <div
      id="add"
      className='my-auto'
      onClick={setShowAddPickModal}
    >
      <FontAwesomeIcon size='xl' icon={faPlus} />
    </div>
  </div>
)

const Tab = ({
  tab,
  active,
  setTab
}: {
  tab: TabType,
  active: boolean,
  setTab: () => void
}) => (
  <li className="nav-item" onClick={setTab}>
    <a className={`nav-link ${active ? 'active' : ''}`} href="#">{tab.label}</a>
  </li>

)
const Panel = ({
  groups,
  status,
  reptile,
  selectGroup,
  selectedGrouping,
  openDetailsModal,
  selectFunction,
  selectedGroups,
  deleteMode,
  beforeDelete,
  initialFetch,
  loading = false,
  isSpecies = false,
  userInfo
}: {
  groups: CollectionReptileGroup[],
  reptile: string,
  status: string,
  selectGroup: (group: CollectionReptileGroup, groupKey: string) => void,
  selectedGrouping: string,
  openDetailsModal: (reptile: CollectionReptile) => void,
  selectFunction: (reptile: CollectionReptile) => void | null,
  selectedGroups: CollectionReptileGroup[],
  deleteMode: boolean,
  beforeDelete: (reptileForDelete: CollectionReptile) => void,
  initialFetch?: boolean,
  loading?: boolean,
  isSpecies?: boolean,
  userInfo: UserInfo
}) => {
  const placeholderCount = localStorage.getItem(`${reptile}${status}Count`) || '0';

  // create array of placeholderCount length
  const placeholderArray = Array.from(Array(parseInt(placeholderCount)).keys());
  // get count of all males, females and unknowns
  const total = selectedGrouping.includes("Name") ? groups.length : groups.reduce((acc, group) => {
    return acc + group.females + group.males + group.unsexed;
  }, 0);

  const isTable = useMemo(() => selectedGrouping === groupings[3].label, [selectedGrouping]);
  return (
    <div>
      <span>Total: {total}</span>
      <div id="grid-container" className="px-2 d-flex flex-wrap">
        {!initialFetch && !isTable && (!groups || Object.keys(groups).length === 0) && <NoGroupItem />}
        {initialFetch && placeholderArray.map(i => (
          <>
            {selectedGrouping === groupings[1].label && <NamedGroupPlaceholder />}
            {selectedGrouping === groupings[0].label && <CollectionGroupPlaceholder />}
          </>
        ))}
        {initialFetch && selectedGrouping === groupings[2].label && <>
          <GenderCollectionGroupPlaceholder sex="male" />
          <GenderCollectionGroupPlaceholder sex="female" />
          <GenderCollectionGroupPlaceholder sex="unsexed" />
        </>}
        {!initialFetch && <>
          {selectedGrouping === groupings[0].label && Object.keys(groups).map((group) => (
            <CollectionGroup
              key={group}
              userInfo={userInfo}
              selectGroup={(groupKey) => selectGroup(groups[group], groupKey)}
              collectionGroup={groups[group]}
              selectFunction={selectFunction}
              selected={selectedGroups.some(selected => selected.reptile_group_id === groups[group].reptile_group_id)}
              isSpecies={isSpecies}
            />
          ))}
          {selectedGrouping === groupings[1].label && Object.keys(groups).map((groupKey) => {
            const group = groups[groupKey];
            return (
              <NamedCollectionGroup
                key={group.id}
                openDetailsModal={openDetailsModal}
                group={group}
                deleteMode={deleteMode}
                beforeDelete={beforeDelete}
                selectFunction={selectFunction}
                loading={loading}
                selected={selectedGroups.some(selected => selected.id === group.id)}
              />
            )
          })}
          {selectedGrouping === groupings[2].label && !initialFetch && Object.keys(groups).map((group) => (
            <GenderCollectionGroup
              key={group}
              selectGroup={(gender) => selectGroup({ ...groups[group], savedReptiles: groups[group].savedReptiles.filter(reptile => reptile.gender === gender) }, gender)}
              collectionGroup={groups[group]}
            />
          ))}
          {selectedGrouping === groupings[3].label && !initialFetch && <CollectionReptileTable
            reptiles={groups}
            openDetailsModal={openDetailsModal}
            selectFunction={selectFunction}
            selectedGroups={selectedGroups}
            beforeDelete={beforeDelete}
            deleteMode={deleteMode}
          />}
        </>}
      </div>
    </div>
  )
}

export const Collection = ({
  userInfo,
  reptile,
  reptiles,
  morphs,
  genes,
  backToMenu,
  changeReptile,
  rep
}: {
  userInfo: UserInfo,
  reptile: string,
  reptiles: Reptile[],
  morphs: Morph[],
  genes: Gene[],
  backToMenu: () => void,
  changeReptile: (reptile: string) => void,
  rep: Reptile
}) => {
  const tabs = [
    { label: "Available", status: "available" },
    { label: "Not For Sale", status: "nfs" },
    { label: "Sold", status: "sold" }
  ];
  const [groups, setGroups] = useState<SortedGroups>({ available: [], nfs: [], sold: [] });
  const [selectedGrouping, setSelectedGrouping] = useState<SelectedGrouping>(localStorage.getItem("groupMethod") || groupings[0].grouping);
  const [activeIndex, setActiveIndex] = useState(parseInt(localStorage.getItem("selectedTab") || '0') || 0);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAddPickModal, setShowAddPickModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [soldReptiles, setSoldReptiles] = useState<CollectionReptile[]>([]);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [editingReptile, setEditingReptile] = useState<CollectionReptile | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<(CollectionReptileGroup | CollectionReptile)[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [fetchingGroups, setFetchingGroups] = useState(true);
  const [initialFetch, setInitialFetch] = useState(true);
  const [moveMode, setMoveMode] = useState(false);
  const [moveDestination, setMoveDestination] = useState("");
  const [calculateMode, setCalculateMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [confirmDeleteReptile, setConfirmDeleteReptile] = useState<CollectionReptile | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPairingModal, setShowPairingModal] = useState(false);
  const {appendControls} = useRepToolContext();
  const [showCollectionStatsModal, setShowCollectionStatsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sexFilter, setSexFilter] = useState<"male" | "female" | "unsexed" | null>(null);

  const selectFunction = (group: CollectionReptileGroup | CollectionReptile) => {
    if (!moveMode && !calculateMode) {
      return;
    }
    const found = selectedGroups.find(selected => selected.id === group.id);
    if (!found) {
      if (selectedGroups.length === 2 && calculateMode) {
        (window as any).alert("You can only select 2 groups at a time");
      } else {
        setSelectedGroups([...selectedGroups, group])
      }
    } else {
      setSelectedGroups(selectedGroups.filter(selected => selected.id !== group.id));
    }
  }
  const calculate = () => {
    if (selectedGroups.length === 0) {
      (window as any).alert("Please select 1 or 2 parents to calculate genetics.")
      return;
    }
    setShowResultsModal(true);
  }
  const openDetailsModal = (reptile) => {
    setEditingReptile(reptile);
    removeControls("Details Modal");
    setShowDetails(true);
  }
  const beforeDelete = (reptileForDelete: CollectionReptile) => {
    setConfirmDeleteReptile(reptileForDelete);
    if (localStorage.getItem("understands-delete")) {
      deleteConfirmReptile();
    } else {
      removeControls("Delete Modal");
      setOpenConfirmation(true);
    }
  }

  const deleteConfirmReptile = () => {
    if (!confirmDeleteReptile) {
      return;
    }
    const groupData = {
      reptile: localStorage.getItem('selectedReptile'),
      status: confirmDeleteReptile.status,
      morphName: confirmDeleteReptile.morph_name,
      males: confirmDeleteReptile.gender === "male" ? 1 : 0,
      females: confirmDeleteReptile.gender === "female" ? 1 : 0,
      unknown: confirmDeleteReptile.gender === "unsexed" ? 1 : 0,
    }

    BasicFetch({
      url: `${(window as any).serverLocation}reptile/collection_reptile/delete`,
      data: confirmDeleteReptile,
      method: 'DELETE',
      onSuccess: (data) => {
        (window as any).alert("Successfully deleted reptile.");

        setFetchingGroups(true);
      },
      onFail: () => {
      },

    })

    BasicFetch({
      url: `${(window as any).serverLocation}/reptile/reptile_group/delete`,
      data: groupData,
      method: 'DELETE',
      onSuccess: (data) => {
        (window as any).alert("Successfully deleted reptile.");
        setFetchingGroups(true);
      },
      onFail: () => {
      },
    })
  }

  const openGroupModal = (group, groupKey) => {
    setSelectedGroupKey(groupKey);
    setSelectedGroup(group);
    setShowGroupModal(true);
    removeControls("Group Modal");
  }
  const updateGroup = (group) => {
    setSelectedGroup(group);
    // TODO: update groups without refetching
    setFetchingGroups(true);
  }

  const getGroups = () => {
    const filterType = groupings.find(grouping => grouping.label === selectedGrouping)?.grouping.toUpperCase() || "MORPH";
    BasicFetch({
      url: `${(window as any).serverLocation}collection_group/reptile/${reptile}/get_all`,
      extra: {
        filterType,
        use_new: true
      },
      method: 'GET',
      onSuccess: (data: { groups: CollectionReptileGroup[], savedReptiles: CollectionReptile[] }) => {
        const sorted = {
          available: [] as (CollectionReptileGroup | CollectionReptile)[],
          sold: [] as (CollectionReptileGroup | CollectionReptile)[],
          nfs: [] as (CollectionReptileGroup | CollectionReptile)[]
        };
        if (filterType !== "NAME") {
          data.groups.forEach((group: CollectionReptileGroup) => {
            if (group.status === "available") {
              sorted.available.push(group);
            } else if (group.status === "sold") {
              sorted.sold.push(group);
            } else if (group.status === "nfs") {
              sorted.nfs.push(group);
            }
          })
        } else {
          // sort by name
          data.savedReptiles.forEach((reptile: CollectionReptile) => {
            if (reptile.status === "available") {
              sorted.available.push(reptile);
            } else if (reptile.status === "sold") {
              sorted.sold.push(reptile);
            } else if (reptile.status === "nfs") {
              sorted.nfs.push(reptile);
            }
          })

          sorted.available.sort((a, b) => {
            const aName = a.name || a.morph_name;
            const bName = b.name || b.morph_name;
            if (aName < bName) {
              return -1;
            }
            if (aName > bName) {
              return 1;
            }
            return 0;
          })
          sorted.sold.sort((a, b) => {
            const aName = a.name || a.morph_name;
            const bName = b.name || b.morph_name;
            if (aName < bName) {
              return -1;
            }
            if (aName > bName) {
              return 1;
            }
            return 0;
          })
          sorted.nfs.sort((a, b) => {
            const aName = a.name || a.morph_name;
            const bName = b.name || b.morph_name;
            if (aName < bName) {
              return -1;
            }
            if (aName > bName) {
              return 1;
            }
            return 0;
          })
        }
        localStorage.setItem(`${reptile}soldCount`, sorted.sold.length.toString());
        localStorage.setItem(`${reptile}availableCount`, sorted.available.length.toString());
        localStorage.setItem(`${reptile}nfsCount`, sorted.nfs.length.toString());

        setGroups(sorted);
        setInitialFetch(false);
        setSelectedGroups([]);
      },
      onFail: () => {
        (window as any).alert("Failed to fetch groups, please try again later.")
      },
      onFinish: () => {
        setFetchingGroups(false);
      }
    });
  }
  useEffect(() => {
    if (!fetchingGroups && !showGroupModal) {
      setFetchingGroups(true);
    }
  }, [reptile, showGroupModal]);

  useEffect(() => {
    addControls();
  }, [selectedGrouping, selectedGroups, showMenu, calculateMode, moveMode, deleteMode, moveDestination]);

  useEffect(() => {
    if (fetchingGroups) {
      getGroups();
    }
  }, [fetchingGroups]);

  useEffect(() => {
    setInitialFetch(true);
    if (!fetchingGroups) {
      setFetchingGroups(true);
    }
  }, [selectedGrouping]);

  useEffect(() => {
    if (soldReptiles && soldReptiles.length > 0) {
      setShowSoldModal(true);
    }
  }, [soldReptiles]);


  const filteredReptiles = useMemo(() => {
    const reptileGroups = {
      available: [] as (CollectionReptileGroup | CollectionReptile)[],
      sold: [] as (CollectionReptileGroup | CollectionReptile)[],
      nfs: [] as (CollectionReptileGroup | CollectionReptile)[]
    };
    const filteredGroups = groups.available.concat(groups.sold).concat(groups.nfs)

    let filteredReptiles: (CollectionReptile | CollectionReptileGroup)[] = [];
    if (selectedGrouping.includes("Name")) {
      filteredReptiles = applySearchFilter(filteredGroups, searchQuery, sexFilter, selectedGrouping);
    } else {
      for (const group of Object.keys(filteredGroups)) {
        const currentGroup = { ...filteredGroups[group] };
        const filteredReps = applySearchFilter(filteredGroups[group].savedReptiles, searchQuery, sexFilter, selectedGrouping);
        if (filteredReps.length > 0) {
          currentGroup.savedReptiles = filteredReps;
          filteredReptiles.push(currentGroup);
        }
      }

    }
    filteredReptiles.forEach((reptile) => {
      if (reptile.status === "available") {
        reptileGroups.available.push(reptile);
      } else if (reptile.status === "sold") {
        reptileGroups.sold.push(reptile);
      } else if (reptile.status === "nfs") {
        reptileGroups.nfs.push(reptile);
      }
    })
    return reptileGroups;
  }, [groups, searchQuery, sexFilter]);


  const addControls = () => {

    if (calculateMode) {
      addCalcControls();
      return;
    }
    if (moveMode) {
      addMoveControls();
      return;
    }
    if (deleteMode) {
      addDeleteControls();
      return;
    }

    if (selectedGroups.length > 0) {
      setSelectedGroups([]);
    }

    const driverObj = driver({
      steps: [
        { element: '#add', popover: { title: 'Add Reptiles', description: 'Here you can browse through all morphs and genes for the selected reptile.' } },
        { element: '#calculator-btn', popover: { title: 'Genetic Calculator', description: 'This is the genetic calculator. To use it first tap 1 or 2 groups to calculate, then tap this button. Selecting 1 wil show the results if both parents are the selected group. To unselect just tap the group again.' } },
        { element: '#reptile-pick', popover: { description: 'Here you can quick change between all currently supported reptiles. Come back here when you want to select a different reptile.' } },
        { element: '#nav-tabs', popover: { description: 'These tabs allow you to navigate between your available, not for sale and sold reptiles. You can also swipe left and right to navigate between tabs.' } },
        { element: '#organizeStyle', popover: { title: 'Organize', description: 'This dropdown will allow you to change how your collection is displayed. Morph will group all reptiles by morph name. Name will ungroup all your animals. Sex will group them all by male, female, or unsexed.' } },
      ]
    });

    appendControls(<React.Fragment>
      <CollectionGroupFilter
        grouping={selectedGrouping}
        changeGrouping={setSelectedGrouping}
        isSpecies={rep.controls?.isSpecies || false}
      />
      <CollectionFloatButtons
        toggleTutorial={() => driverObj.drive()}
        setShowMenu={() => setShowMenu(!showMenu)}
        setShowAddPickModal={() => { setShowAddPickModal(true); removeControls("Add Pick Modal"); }}
      />
    </React.Fragment>, "Collection");
  }

  const removeControls = (modalName: string) => {
    appendControls(null, modalName);
  }

  const addCalcControls = () => {
    appendControls(
      <div className="d-flex flex-row justify-content-center w-100">
        <Button
          variant="outline-light"
          onClick={() => {
            setCalculateMode(false);
            setSelectedGroups([]);
          }}
          className="col-4 col-lg-3"
        >
          Cancel
        </Button>
        <Button
          variant="outline-light"
          onClick={() => calculate()}
          className="ml-4 col-4 col-lg-3"
          disabled={selectedGroups.length === 0}
        >
          Calculate
        </Button>
      </div>
      , "Calculate Genetics");
  }
  const moveAction = (selectedReptiles: CollectionReptile[]) => {
    const url = `${(window as any).serverLocation}collection_reptile/move/destination/${moveDestination}`;
    BasicFetch({
      url,
      data: {
        reptiles: selectedReptiles,
      },
      method: 'POST',
      onSuccess: (data) => {
        setSelectedGroups([]);
        addControls();
        setFetchingGroups(true);
      },
      onFail: () => {
      },
    })
  }
  const actionClicked = () => {
    const currentReptiles = (selectedGroups as CollectionReptile[]);
    if (moveDestination === "sold") {
      setSoldReptiles(currentReptiles)
    } else {
      moveAction(currentReptiles);
    }
  }
  const addMoveControls = () => {
    appendControls(
      <div className="d-flex flex-row justify-content-around w-100">
        <div className='my-auto'>
          <FontAwesomeIcon icon={faTimes} size='xl' onClick={() => setMoveMode(false)} />
        </div>

        <div className='d-flex'>
          <select onChange={(event) => setMoveDestination(event.target.value)} value={moveDestination} className="h-100">
            <option>Move selected to ...</option>
            <option value="available">Mark as Available</option>
            <option value="nfs">Mark as Not For Sale</option>
            <option value="sold">Mark as Sold</option>
          </select>
          <Button
            variant="outline-light"
            onClick={() => {
              if (selectedGroups.length === 0) {
                (window as any).alert("Please select a reptile to move.");
                return;
              }
              actionClicked();
            }}
            className="ml-2"
          >
            Move
          </Button>
        </div>

      </div>
      , "Move Reptiles");
  }

  const addDeleteControls = () => {
    appendControls(
      <div className="d-flex flex-row justify-content-around w-100">
        <Button
          variant="outline-light"
          onClick={() => setDeleteMode(false)}
          className="ml-2"
        >
          Cancel
        </Button>
      </div>
      , "Delete Reptiles");
  }


  const pickReptile = () => {
    setShowMenu(false);
    (window as any).setUpReptileSelect('', () => (reptile: string) => {
      changeReptile(reptile);
    }, localStorage.getItem('selectedReptile') || '')
  }
  const hasSubscription = useMemo(() => {
    return userInfo.subscription?.subscription_type !== "FREE";
  }, [userInfo.subscription]);
  return (
    <div className='fadeIn animated'>
      <Offcanvas className='bg-black border-right px-4 text-light' show={showMenu} onHide={() => setShowMenu(false)}>
        <Offcanvas.Header className='text-light d-flex justify-content-end' >
          <span className='close-canvas' onClick={() => setShowMenu(false)}>
            <FontAwesomeIcon icon={faTimes} size='2x' />
          </span>
        </Offcanvas.Header>

        <div className='d-flex flex-column mt-3'>
          <h5>Select Reptile</h5>
          <Button variant="outline-light" onClick={pickReptile} className='w-100'>
            {rep.reptile_name}
          </Button>
        </div>

        <div>
          {selectedGrouping.includes("Name") && <>
            <h5 className='mt-4'>Manage Collection</h5>
            <ToolMenuButton
              userInfo={userInfo}
              clickFunction={() => {
                setMoveMode(!moveMode);
                setShowMenu(false);
              }}>
              {moveMode ? "Cancel Move" : "Move / Sell Reptiles"}
            </ToolMenuButton>

            <Button variant="outline-danger" onClick={() => {
              setDeleteMode(!deleteMode);
              setShowMenu(false);
            }} className='w-100'>
              {deleteMode ? "Cancel Delete" : "Delete Reptiles"}
            </Button>
          </>}
          <h5 className='mt-4'>QR Tools</h5>
          <ToolMenuButton
            userInfo={userInfo}
            clickFunction={() => {
              setShowMenu(false);
              setShowLabelModal(true);
            }} >
            Create Labels
          </ToolMenuButton>
          {false && <ToolMenuButton
            userInfo={userInfo}
            clickFunction={() => {
              setShowMenu(false);
              setShowScanModal(true);
            }} >
            Scan and Record
          </ToolMenuButton>}
          <h5 className='mt-2'>Other Tools</h5>

          {!rep.controls?.isSpecies && <ToolMenuButton
            userInfo={userInfo}
            clickFunction={() => {
              setCalculateMode(!calculateMode);
              if (!calculateMode) {
                (window as any).alert("Select 1 or 2 groups to calculate genetics.");
              }
              setShowMenu(false);
            }}
          >
            {calculateMode ? "Cancel Calculate" : "Calculate Genetics"}
          </ToolMenuButton>}
          <ToolMenuButton
            userInfo={userInfo}
            clickFunction={() => {
              setShowMenu(false);
              setShowPairingModal(true);
            }
            }>
            Manage Pairings
          </ToolMenuButton>
          <ToolMenuButton
            userInfo={userInfo}
            clickFunction={() => {
              setShowMenu(false);
              setShowCollectionStatsModal(true);
            }} subscriberOnly={!hasSubscription}>
            Collection Stats
          </ToolMenuButton>
          <ToolMenuButton
            userInfo={userInfo}
            clickFunction={() => {
              setShowMenu(false);
              setShowShareModal(true);
            }} subscriberOnly={!hasSubscription}>
            Share Available Reptiles
          </ToolMenuButton>
        </div>

      </Offcanvas>
      {showScanModal && <ScanModal closeModal={() => { setShowScanModal(false); addControls() }} reptiles={reptiles} />}
      {showLabelModal && <LabelModal closeModal={() => { setShowLabelModal(false); addControls(); }} reptiles={reptiles} />}
      {showShareModal && <ShareAvailableReptilesModal closeModal={() => { setShowShareModal(false); addControls() }} />}
      {showCollectionStatsModal && <CollectionStatsModal closeModal={() => { setShowCollectionStatsModal(false); addControls(); }} />}
      {openConfirmation && <ConfirmDeleteModal
        closeModal={() => { setOpenConfirmation(false); addControls() }}
        confirmDeleteReptile={confirmDeleteReptile}
        morphs={morphs}
        deleteFunction={deleteConfirmReptile}
      />}

      {showDetails && !!editingReptile && <CollectionReptileModal
        userInfo={userInfo}
        closeModal={() => { setShowDetails(false); addControls(); }}
        reptileId={editingReptile.uuid}
        morphs={morphs}
        genes={genes}
        refreshReptile={(currentReptile: CollectionReptile) => {
          if (selectedGrouping === groupings[1].label) {
            const newGroups = { ...groups };
            const group = newGroups[tabs[activeIndex].status];
            const reptileIndex = group.findIndex((reptile) => reptile.id === currentReptile.id);
            group[reptileIndex] = currentReptile;
            setGroups(newGroups);
          }
        }}
        readOnly={false}
        isSpecies={rep.controls?.isSpecies || false}
      />}
      {
        showPairingModal && <AllPairingsModal
          userInfo={userInfo}
          closeModal={() => { setShowPairingModal(false); addControls(); }}
        />
      }
      {showResultsModal && <ResultsModal
        closeModal={() => { setShowResultsModal(false); addControls(); }}
        parents={selectedGroups}
        morphs={morphs}
        genes={genes}
      />}
      {showSoldModal && <SoldModal
        morphs={morphs}
        closeModal={() => { setShowSoldModal(false); addControls(); }}
        onMove={() => {
          setFetchingGroups(true);
          setShowGroupModal(false);
          addControls();
          setShowSoldModal(false);
          setSoldReptiles([]);
        }}
        reptiles={soldReptiles}
      />}

      {showGroupModal && <GroupModal
        closeModal={() => { setShowGroupModal(false); addControls(); }}
        group={selectedGroup}
        updateGroup={updateGroup}
        morphs={morphs}
        genes={genes}
        status={tabs[activeIndex].status}
        filterValue={selectedGroupKey || ""}
        setSoldReptiles={setSoldReptiles}
        isSpecies={rep.controls?.isSpecies || false}
      />}
      {
        showAddPickModal && <AddPickModal
          isSpecies={rep.controls?.isSpecies}
          genes={genes}
          status={tabs[activeIndex].status}
          morphs={morphs}
          closeModal={() => { setShowAddPickModal(false); addControls(); }}
          updateGroups={updateGroup}
        />
      }

      <div>
        <ModuleTitle title={"Collection"} backToMenu={backToMenu} />
        <InputGroup className='px-3'>
          <FormControl
            className='flex-fill mx-auto my-3 bg-dark text-white'
            placeholder='Search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {selectedGrouping.includes("Name") && <RepToolDropdown
            className='mx-auto my-3'
            toggleComponent={<>{
              toTitleCase(sexFilter || "All")
            }</>}
            options={[
              {
                label: "All",
                onClick: () => setSexFilter(null)
              },
              {
                label: "Males",
                onClick: () => setSexFilter("male")
              },
              {
                label: "Females",
                onClick: () => setSexFilter("female")
              },
              {
                label: "Unsexed",
                onClick: () => setSexFilter("unsexed")
              }
            ]}
          />}
        </InputGroup>
        <ul id='nav-tabs' className="nav nav-tabs">
          {tabs.map((tab, index) => (
            <Tab key={tab.label} setTab={() => { setActiveIndex(index); localStorage.setItem("selectedTab", index.toString()) }} active={index === activeIndex} tab={tab} />
          ))}
        </ul>
        <div id='collection-list-container' className="row">
          <div id='collection-container' className='w-100'>
            <Carousel
              className='h-100 mt-3'
              showIndicators={false}
              selectedItem={activeIndex}
              showThumbs={false}
              preventMovementUntilSwipeScrollTolerance={true}
              showStatus={false}
              showArrows={false}
              swipeScrollTolerance={30}
              onChange={(index) => { setActiveIndex(index); localStorage.setItem("selectedTab", index.toString()) }}
            >
              {tabs.map((tab) => (
                <Panel
                  key={tab.label}
                  status={tab.status}
                  reptile={reptile}
                  morphs={morphs}
                  userInfo={userInfo}
                  beforeDelete={beforeDelete}
                  selectFunction={(moveMode || calculateMode) ? selectFunction : null}
                  selectedGroups={selectedGroups}
                  selectedGrouping={selectedGrouping}
                  selectGroup={(group, groupKey) => openGroupModal(group, groupKey)}
                  groups={filteredReptiles[tab.status]}
                  openDetailsModal={openDetailsModal}
                  deleteMode={deleteMode}
                  loading={fetchingGroups}
                  initialFetch={initialFetch}
                  isSpecies={rep.controls?.isSpecies}
                />
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  )
}